(() => {
  'use strict';
  const storageKey = 'framepace.saved-designs.v1';
  const saveNoticeKey = 'framepace.saved-designs.notice-seen.v1';
  let saved = [];
  let persistent = true;
  let saveNoticeSeen = false;
  let saveNoticeTrigger;
  let dialog;
  let renderVersion = 0;
  const productCache = new Map();
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  function imageUrl(value) {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : '';
    } catch { return ''; }
  }
  function cleanSaved(value) {
    if (!Array.isArray(value)) return [];
    const unique = new Set();
    return value.filter(item => item && validHandle(item.handle) && !unique.has(item.handle) && unique.add(item.handle)).slice(0,100).map(item => ({
      handle:item.handle, title:String(item.title || item.handle).slice(0,300), image:imageUrl(item.image || '')
    }));
  }
  try { saved = cleanSaved(JSON.parse(localStorage.getItem(storageKey) || '[]')); } catch { persistent = false; }
  try { saveNoticeSeen = localStorage.getItem(saveNoticeKey) === '1'; } catch { /* Keep the existing storage fallback. */ }
  const has = handle => saved.some(item => item.handle === handle);
  function announce(message) {
    const status = document.querySelector('[data-fp-save-status]');
    if (status) status.textContent = message;
  }
  function syncButtons(scope = document) {
    scope.querySelectorAll('[data-fp-save]').forEach(button => {
      const selected = has(button.dataset.handle);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', `${selected ? 'Remove saved' : 'Save'} ${button.dataset.title}`);
      const label = button.querySelector('[data-fp-save-label]');
      if (label) label.textContent = selected ? 'Saved for later' : 'Save for later';
    });
    document.querySelectorAll('[data-fp-saved-open]').forEach(button => {
      button.hidden = false;
      button.setAttribute('aria-label', `Saved designs${saved.length ? ` (${saved.length})` : ''}`);
    });
    document.querySelectorAll('[data-fp-saved-count]').forEach(count => {
      count.textContent = saved.length;
      count.hidden = saved.length === 0;
    });
  }
  function writeSaved() {
    try { localStorage.setItem(storageKey,JSON.stringify(saved)); } catch { persistent = false; }
    syncButtons();
    const note = document.getElementById('FPSavedNote');
    if (note) note.textContent = persistent ? 'Saved on this device. No account needed.' : 'Your browser is blocking saved storage. These designs will stay here for this visit only.';
  }
  function positionSaveNotice() {
    const notice = document.querySelector('[data-fp-save-notice]');
    if (!notice || notice.hidden) return;
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    // Keep the header heart visible when the header is on screen.
    notice.style.top = `${Math.max(16, Math.min(headerBottom + 12, window.innerHeight - notice.offsetHeight - 16))}px`;
  }
  function showSaveNotice(trigger) {
    const notice = document.querySelector('[data-fp-save-notice]');
    if (saveNoticeSeen || !persistent || !notice) return false;
    saveNoticeSeen = true;
    try { localStorage.setItem(saveNoticeKey, '1'); } catch { /* Show once during this page visit if the flag cannot be stored. */ }
    saveNoticeTrigger = trigger;
    notice.hidden = false;
    positionSaveNotice();
    return true;
  }
  function dismissSaveNotice() {
    const notice = document.querySelector('[data-fp-save-notice]');
    if (!notice || notice.hidden) return;
    const restoreFocus = notice.contains(document.activeElement);
    notice.hidden = true;
    if (restoreFocus && saveNoticeTrigger?.isConnected) saveNoticeTrigger.focus({preventScroll:true});
  }
  const rootPath = () => (dialog?.dataset.root || '/').replace(/\/?$/, '/');
  const productUrl = handle => `${rootPath()}products/${handle}`;
  function money(product) {
    if (!product.available) return 'Currently unavailable';
    if (!Number.isFinite(product.price)) return 'See current price';
    const currency = dialog?.dataset.currency || 'USD';
    try {
      return `${product.price_varies ? 'From ' : ''}${new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency}).format(product.price / 100)}`;
    } catch { return 'See current price'; }
  }
  async function getProduct(path) {
    const url = new URL(path,location.origin);
    if (url.origin !== location.origin || !/\/products\/[a-z0-9-]+\/?$/.test(url.pathname)) throw new Error('Invalid product link');
    url.pathname = url.pathname.replace(/\/$/,'') + '.js';
    url.search = '';
    url.hash = '';
    const key = url.href;
    const cached = productCache.get(key);
    if (cached && Date.now() - cached.created > 30000) productCache.delete(key);
    if (!productCache.has(key)) {
      const request = fetch(key, {headers:{Accept:'application/json'}, signal:AbortSignal.timeout(10000)})
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
        .catch(error => { productCache.delete(key); throw error; });
      productCache.set(key,{request,created:Date.now()});
    }
    return productCache.get(key).request;
  }
  function node(tag,className,text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }
  async function renderSaved() {
    if (!dialog) return;
    const version = ++renderVersion;
    const list = dialog.querySelector('[data-fp-saved-list]');
    list.replaceChildren();
    dialog.querySelector('[data-fp-saved-empty]').hidden = saved.length > 0;
    await Promise.all(saved.map(async item => {
      const li = node('li','fp-saved-item');
      const link = node('a');
      link.href = productUrl(item.handle);
      if (item.image) {
        const img = node('img');
        img.src = item.image;
        img.alt = '';
        img.width = 360;
        img.height = 360;
        img.loading = 'lazy';
        link.append(img);
      }
      link.append(node('span','',item.title));
      const price = node('p','fp-saved-price','Checking current price…');
      const remove = node('button','fp-saved-remove','Remove');
      remove.type = 'button';
      remove.dataset.fpSavedRemove = item.handle;
      remove.setAttribute('aria-label',`Remove ${item.title} from saved designs`);
      li.append(link,price,remove);
      list.append(li);
      try {
        const product = await getProduct(link.href);
        if (version === renderVersion) price.textContent = money(product);
      } catch {
        if (version === renderVersion) price.textContent = 'Price unavailable — open design for details';
      }
    }));
  }
  // Describe only attributes explicitly present in this product's own description.
  function garmentFacts(product) {
    const doc = new DOMParser().parseFromString(product.description || '', 'text/html');
    const text = doc.body.textContent.replace(/\s+/g,' ');
    const model = text.match(/Comfort Colors[®\s]*(?:1717|9360)|Gildan[®\s]+(?:Softstyle[®\s]+)?64000L?/i)?.[0];
    const fit = text.match(/\b(?:semi-fitted|(?:relaxed|regular(?: unisex)?|classic|loose|slim)[ -]fit(?:ted)?)\b/i)?.[0];
    const weight = text.match(/\b\d{2,3}(?:\.\d+)?\s*g\s*\/\s*m[²2]/i)?.[0];
    const cotton = text.match(/100%\s+(?:combed\s+(?:and\s+)?)?(?:ring[ -]spun\s+)?cotton/i)?.[0];
    const fabric = /polyester|heather|blend/i.test(text) ? 'Fabric varies by color; see product details.' : cotton;
    return { model, details:[fit,weight].filter(Boolean).join(' · '), fabric };
  }
  function mountComparison(root, config) {
    const guidance = root.closest('.product__info-container')?.querySelector('[data-fp-pdp-guidance]');
    const comparisonHost = guidance || root;
    if (comparisonHost.querySelector('.fp-garment-comparison')) return;
    const choices = (config.options || []).flatMap(option => option.values || []).filter(value => {
      if (!value || typeof value !== 'object' || !value.url) return false;
      try { const url = new URL(value.url,location.origin); return url.origin === location.origin && /\/products\/[a-z0-9-]+\/?$/.test(url.pathname); } catch { return false; }
    });
    if (choices.length < 2) return;
    const details = node('div','fp-garment-comparison');
    const summary = node('button','fp-compare-toggle','Compare fit, fabric & price');
    summary.type = 'button';
    details.id = `FPComparison-${root.closest('product-info')?.id || 'product'}`;
    details.hidden = true;
    summary.setAttribute('aria-controls',details.id);
    summary.setAttribute('aria-expanded','false');
    const list = node('ul','fp-compare-list');
    const note = node('p','fp-comparison-note','Prices shown are starting prices where sizes or colors vary. Check the size guide for the garment you choose.');
    details.append(list,note);
    let loaded = false;
    summary.addEventListener('click',async () => {
      details.hidden = !details.hidden;
      summary.setAttribute('aria-expanded',String(!details.hidden));
      if (details.hidden || loaded) return;
      loaded = true;
      await Promise.all(choices.map(async choice => {
        const url = new URL(choice.url,location.origin);
        url.search = '';
        url.hash = '';
        const li = node('li','fp-compare-item');
        const link = node('a','',choice.label);
        link.href = url.href;
        if (url.pathname.replace(/\/$/,'') === location.pathname.replace(/\/$/,'')) {
          li.setAttribute('aria-current','true');
          link.append(' · Selected');
        }
        const price = node('p','','Loading…');
        price.setAttribute('role','status');
        li.append(link,price);
        list.append(li);
        // Use the existing selector so switching garments retains the chosen size.
        link.addEventListener('click',event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const button = Array.from(root.querySelectorAll('.fp-option-btn')).find(button => button.dataset.fpTargetPath === url.pathname);
          if (button) { event.preventDefault(); button.click(); }
        });
        try {
          const product = await getProduct(url.href);
          const facts = garmentFacts(product);
          price.textContent = money(product);
          if (facts.model) li.append(node('strong','',facts.model));
          if (facts.details) li.append(node('p','',facts.details));
          if (facts.fabric) li.append(node('p','',facts.fabric));
          if (!facts.model && !facts.details && !facts.fabric) li.append(node('p','','See this garment’s product details and size guide.'));
        } catch { price.textContent = 'Open this garment for current details.'; }
      }));
    });
    comparisonHost.append(summary,details);
  }
  window.FramePaceUX = {mountComparison};
  document.addEventListener('DOMContentLoaded', () => {
    dialog = document.querySelector('[data-fp-saved-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    syncButtons();
    document.addEventListener('click',event => {
      const button = event.target.closest('[data-fp-save]');
      if (button) {
        event.preventDefault();
        const handle = button.dataset.handle;
        if (!validHandle(handle)) return;
        const selected = has(handle);
        if (!selected && saved.length >= 100) { announce('Your saved list is full. Remove a design to save another.'); return; }
        saved = selected ? saved.filter(item => item.handle !== handle) : [...saved,{handle,title:button.dataset.title,image:imageUrl(button.dataset.image)}];
        writeSaved();
        const noticeShown = !selected && showSaveNotice(button);
        if (selected && saved.length === 0) dismissSaveNotice();
        announce(noticeShown ? 'Saved for later. Find your picks using the heart at the top.' : selected ? 'Design removed from your saved list.' : persistent ? 'Design saved for later.' : 'Design saved for this visit. Browser storage is unavailable.');
      }
      if (event.target.closest('[data-fp-save-notice-close]')) dismissSaveNotice();
      if (event.target.closest('[data-fp-saved-open]')) {
        if (dialog.open) return;
        dismissSaveNotice();
        dialog.showModal();
        document.body.classList.add('fp-saved-open');
        const note = dialog.querySelector('#FPSavedNote');
        if (!persistent) note.textContent = 'Your browser is blocking saved storage. These designs will stay here for this visit only.';
        renderSaved();
      }
      if (event.target.closest('[data-fp-saved-close]')) dialog.close();
      const remove = event.target.closest('[data-fp-saved-remove]');
      if (remove) {
        saved = saved.filter(item => item.handle !== remove.dataset.fpSavedRemove);
        writeSaved();
        renderSaved();
        dialog.querySelector('[data-fp-saved-close]').focus();
        announce('Design removed from your saved list.');
      }
    });
    document.addEventListener('keydown',event => {
      if (event.key === 'Escape') dismissSaveNotice();
    });
    window.addEventListener('resize',positionSaveNotice);
    window.addEventListener('scroll',positionSaveNotice,{passive:true});
    dialog.addEventListener('click',event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('close',() => document.body.classList.remove('fp-saved-open'));
    window.addEventListener('storage',event => {
      if (event.key === saveNoticeKey || event.key === null) {
        saveNoticeSeen = event.newValue === '1';
        if (event.key === saveNoticeKey) return;
      }
      if (event.key !== storageKey && event.key !== null) return;
      try { saved = cleanSaved(JSON.parse(event.newValue || '[]')); } catch { saved = []; }
      syncButtons();
      if (dialog.open) renderSaved();
    });
    // Collection filtering, pagination and quick views can replace cards without a page load.
    new MutationObserver(records => {
      if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('[data-fp-save], [data-fp-saved-open]') || node.querySelector('[data-fp-save], [data-fp-saved-open]'))))) syncButtons();
    }).observe(document.body,{childList:true,subtree:true});
  });
})();
